/**
 * What-If Simulator (Requirement 167 - Pricing Engine)
 * Compares two real, live-or-cached SKU prices from the provider's own
 * public price API - not an estimate re-projected from a stored number.
 * Deliberately explicit-SKU-input (not auto-wired from a recommendation's
 * vague "next size down" text) - see pricing-catalog.service.ts for why
 * that data isn't precise enough to drive a real price lookup honestly.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { getActiveTenantId } from '../services/tenantContext';

const COST_API_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : 'http://localhost:3002/api/v1';

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

type Provider = 'aws' | 'azure' | 'gcp';

interface SimulateResult {
  from: { sku: string; unitPrice: number; unitOfMeasure: string; currency: string; source: 'cache' | 'live' };
  to: { sku: string; unitPrice: number; unitOfMeasure: string; currency: string; source: 'cache' | 'live' };
  currentMonthlyCost: number;
  projectedMonthlyCost: number;
  monthlySavings: number;
  annualSavings: number;
  discountPercentApplied: number;
}

const SKU_PLACEHOLDER: Record<Provider, { sku: string; region: string }> = {
  aws: { sku: 'm5.xlarge', region: 'us-east-1' },
  azure: { sku: 'Standard_D4s_v5', region: 'eastus' },
  gcp: { sku: 'n2-standard-4', region: 'us-central1' },
};

export const WhatIfSimulatorPage: React.FC = () => {
  const [provider, setProvider] = useState<Provider>('aws');
  const [region, setRegion] = useState(SKU_PLACEHOLDER.aws.region);
  const [fromSku, setFromSku] = useState('');
  const [toSku, setToSku] = useState('');
  const [hoursPerMonth, setHoursPerMonth] = useState(730);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulateResult | null>(null);

  const handleProviderChange = (p: Provider) => {
    setProvider(p);
    setRegion(SKU_PLACEHOLDER[p].region);
    setResult(null);
    setError(null);
  };

  const handleSimulate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromSku.trim() || !toSku.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        `${COST_API_URL}/pricing/simulate`,
        { provider, region, fromSku: fromSku.trim(), toSku: toSku.trim(), hoursPerMonth },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' } }
      );
      setResult(res.data?.data || null);
    } catch (e: any) {
      setError(e.response?.data?.errors?.[0]?.message || e.message || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-foreground">What-If Simulator</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Compare two real SKU prices, pulled live from the provider's own public price API (cached for a week),
          with your negotiated discount applied.
        </p>
      </div>

      <div className="rounded-lg bg-card p-6 shadow dark:bg-card">
        <form onSubmit={handleSimulate} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Provider</label>
            <select
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value as Provider)}
              className="w-full rounded-lg border border-border px-4 py-2 dark:border-border dark:bg-secondary dark:text-white"
            >
              <option value="aws">AWS</option>
              <option value="azure">Azure</option>
              <option value="gcp">GCP (not yet configured - needs a platform billing API key)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Region</label>
            <input
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder={SKU_PLACEHOLDER[provider].region}
              className="w-full rounded-lg border border-border px-4 py-2 dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Current SKU</label>
            <input
              type="text"
              value={fromSku}
              onChange={(e) => setFromSku(e.target.value)}
              placeholder={SKU_PLACEHOLDER[provider].sku}
              required
              className="w-full rounded-lg border border-border px-4 py-2 dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Proposed SKU</label>
            <input
              type="text"
              value={toSku}
              onChange={(e) => setToSku(e.target.value)}
              required
              className="w-full rounded-lg border border-border px-4 py-2 dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">Hours/month</label>
            <input
              type="number"
              value={hoursPerMonth}
              onChange={(e) => setHoursPerMonth(Number(e.target.value))}
              min={1}
              max={744}
              className="w-full rounded-lg border border-border px-4 py-2 dark:border-border dark:bg-secondary dark:text-white"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Simulating...' : 'Run Simulation'}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-4 rounded border border-rose-200 bg-rose-50 px-4 py-2 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-6 grid grid-cols-1 gap-4 border-t pt-6 dark:border-border md:grid-cols-2">
            <div>
              <div className="text-xs uppercase text-muted-foreground">Current</div>
              <div className="text-lg font-semibold text-foreground">
                {result.from.sku} - ${result.from.unitPrice.toFixed(4)}/{result.from.unitOfMeasure}
              </div>
              <div className="text-sm text-muted-foreground">${result.currentMonthlyCost.toFixed(2)}/month</div>
              <div className="text-xs text-muted-foreground/70">{result.from.source === 'cache' ? 'from cache' : 'fetched live'}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Proposed</div>
              <div className="text-lg font-semibold text-foreground">
                {result.to.sku} - ${result.to.unitPrice.toFixed(4)}/{result.to.unitOfMeasure}
              </div>
              <div className="text-sm text-muted-foreground">${result.projectedMonthlyCost.toFixed(2)}/month</div>
              <div className="text-xs text-muted-foreground/70">{result.to.source === 'cache' ? 'from cache' : 'fetched live'}</div>
            </div>
            <div className="md:col-span-2 rounded-lg bg-emerald-50 p-4 dark:bg-emerald-900/20">
              <div className="text-sm text-emerald-800 dark:text-emerald-300">
                Estimated savings: <span className="font-bold">${result.monthlySavings.toFixed(2)}/month</span> (
                ${result.annualSavings.toFixed(2)}/year)
                {result.discountPercentApplied > 0 && ` - includes your ${result.discountPercentApplied}% negotiated discount`}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
